<template>
  <div class="tag-anchor" :style="styles">
    <div class="tag-line" :style="lineStyle"></div>

    <div v-if="tailPosition !== 'none'" class="tag-dot" :style="{ backgroundColor: tailColourEffective }" />

    <div class="tag-action" :class="[`tag-action--${type}`, { 'tag-action--pulse': shouldPulse }]" ref="el" :style="labelStyle" @click="triggerAction">
      <fa :icon="icon" fixed-width />
      <div class="tag-action__ring"></div>
    </div>
  </div>
</template>

<script>
export default {
  props: ["action", "background-colour", "icon", "label", "pos", "tail-height", "tail-position", "tail-colour", "type", "tag-scale"],

  computed: {
    lang() {
      return this.$store.state.language.code;
    },

    styles() {
      return {
        left: `${this.pos.x / 20}em`,
        top: `${this.pos.y / 20}em`,
      };
    },

    tailColourEffective() {
      return this.tailColour || this.backgroundColour;
    },

    scaleEffective() {
      const raw = Number(this.tagScale);
      const s = Number.isFinite(raw) ? raw : 1;
      return Math.min(2, Math.max(0.5, s));
    },

    // scale from tail connection point (same mapping as dashboard)
    actionTransformOrigin() {
      switch (this.tailPosition) {
        case "bottom-left":
          return "0% 100%";
        case "bottom-center":
          return "50% 100%";
        case "bottom-right":
          return "100% 100%";

        case "top-left":
          return "0% 0%";
        case "top-center":
          return "50% 0%";
        case "top-right":
          return "100% 0%";

        case "left-center":
          return "0% 50%";
        case "right-center":
          return "100% 50%";

        case "none":
        default:
          return "50% 50%";
      }
    },

    labelStyle() {
      let tailStyles = {};

      switch (this.tailPosition) {
        case "none":
          tailStyles = {
            transform: `translate(-50%, -50%)`,
          };
          break;

        case "bottom-left":
          tailStyles = {
            bottom: "0",
            transform: `translate(-1em, -${this.tailHeight * 1.33}em)`,
          };
          break;

        case "bottom-center":
          tailStyles = {
            bottom: "0",
            left: "50%",
            transform: `translate(-50%, -${this.tailHeight * 1.33}em)`,
          };
          break;

        case "bottom-right":
          tailStyles = {
            bottom: "0",
            right: "0",
            transform: `translate(1em, -${this.tailHeight * 1.33}em)`,
          };
          break;

        case "top-left":
          tailStyles = {
            transform: `translate(-1em, ${this.tailHeight * 1.33}em)`,
          };
          break;

        case "top-center":
          tailStyles = {
            left: "50%",
            transform: `translate(-50%, ${this.tailHeight * 1.33}em)`,
          };
          break;

        case "top-right":
          tailStyles = {
            right: "0",
            transform: `translate(1em, ${this.tailHeight * 1.33}em)`,
          };
          break;

        case "left-center":
          tailStyles = {
            left: "0",
            top: ".125em",
            transform: `translate(${this.tailHeight * 1.33}em, -50%)`,
          };
          break;

        case "right-center":
          tailStyles = {
            right: "0",
            top: ".125em",
            transform: `translate(-${this.tailHeight * 1.33}em, -50%)`,
          };
          break;

        default:
          return "";
      }

      //  SCALE ONLY THE ACTION, FROM TAIL CONNECTION POINT
      tailStyles.transform = `${tailStyles.transform} scale(${this.scaleEffective})`;
      tailStyles.transformOrigin = this.actionTransformOrigin;

      let colorStyles = {};
      if (this.backgroundColour && this.backgroundColour !== "#ffffff") {
        colorStyles = {
          backgroundColor: this.backgroundColour,
        };
      }

      return Object.assign(colorStyles, tailStyles);
    },

    lineStyle() {
      const colour = `background-color: ${this.tailColourEffective};`;

      switch (this.tailPosition) {
        case "none":
          return `display: none;`;

        case "bottom-left":
        case "bottom-center":
        case "bottom-right":
          return `
            ${colour}
            height: ${this.tailHeight}em;
            transform: translateY(-${this.tailHeight}em);
            z-index: 6;
          `;

        case "top-left":
        case "top-center":
        case "top-right":
          return `
            ${colour}
            bottom: 0;
            height: ${this.tailHeight}em;
            transform: translateY(${this.tailHeight}em);
            z-index: 6;
          `;

        case "left-center":
          return `
            ${colour}
            height: .25em;
            right: 0;
            top: -.125em;
            width: ${this.tailHeight}em;
            transform: translateX(0);
            z-index: 6;
          `;

        case "right-center":
          return `
            ${colour}
            height: .25em;
            left: 0;
            top: -.125em;
            width: ${this.tailHeight}em;
            transform: translateX(-${this.tailHeight}em);
            z-index: 6;
          `;

        default:
          return "";
      }
    },

    shouldPulse() {
      return !!(this.action && this.action.method);
    },
  },

  methods: {
    triggerAction() {
      if (this.action && this.action.method) {
        this.$store.dispatch(`actions/${this.action.method}`, {
          payload: this.action.payload,
          virtualTourUrl: this.$attrs.virtualTourUrl,
          tag: {
            icon: this.icon,
            label: this.label,
            backgroundColour: this.backgroundColour,
          },
          meta: this.action.meta,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tag-anchor {
  font-size: 1em;
  opacity: 0;
  position: absolute;
  pointer-events: none;
  z-index: var(--tag-action-z-index);

  &.enter {
    opacity: 1;
    animation: bounce-in 1s 1;
  }

  &.leave {
    opacity: 0;
    transition: opacity 0.3s;
  }
}

.tag-dot {
  background: #fff;
  border-radius: 50%;
  bottom: -0.25em;
  height: 0.5em;
  left: -0.25em;
  pointer-events: auto;
  position: absolute;
  width: 0.5em;
}

.tag-line {
  background: #fff;
  left: -0.125em;
  pointer-events: auto;
  position: absolute;
  width: 0.25em;
}

.tag-action {
  align-items: center;
  background-color: var(--brand-color);
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: var(--tag-box-shadow);
  color: var(--brand-text-color);
  display: flex;
  font-size: 0.75em;
  font-weight: var(--font-weight-secondary);
  height: 4.5em;
  justify-content: center;
  line-height: 1.1;
  padding: 1em;
  pointer-events: auto;
  position: absolute;
  user-select: none;
  width: 4.5em;

  svg {
    height: 100%;
    width: 100%;
  }

  .tag-action__ring {
    border: 0.2rem solid currentColor;
    border-radius: 4rem;
    height: 2.6rem;
    width: 2.6rem;
    position: absolute;
    left: 50%;
    top: 50%;
    animation: none; // no animation by default
    animation-iteration-count: infinite;
    opacity: 0;
    z-index: -1;
  }
}

.tag-action--pulse {
  .tag-action__ring {
    animation: pulsate 2s ease-out infinite;
  }
}
</style>
